import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Check, MapPin, Pencil, Plus, Save, X } from "lucide-react";
import { toast } from "react-toastify";
import useBhandarewala from "../../../services/Order/UseBhandarewala";

const EMPTY_FORM = {
  delivery_area: "",
  delivery_pincode: "",
  delivery_price: "",
  delivery_distance: "",
  is_active: "Y",
};

const getLocationId = (item) =>
  item?.id ??
  item?.delivery_location_id ??
  item?.pk_delivery_location_id ??
  item?.pk_id;

const getArea = (item) =>
  item?.delivery_area ?? item?.deliveryArea ?? item?.area ?? "";

const getPincode = (item) =>
  item?.delivery_pincode ?? item?.deliveryPincode ?? item?.pincode ?? "";

const getPrice = (item) =>
  item?.delivery_price ?? item?.deliveryPrice ?? item?.price ?? "";

const getDistance = (item) =>
  item?.delivery_distance ?? item?.deliveryDistance ?? item?.distance ?? "";

const getActive = (item) =>
  String(item?.is_active ?? item?.isActive ?? item?.active_status ?? "Y")
    .trim()
    .toUpperCase() === "Y"
    ? "Y"
    : "N";

const LocationSettings = ({ userID, refreshKey = 0 }) => {
  const {
    getDeliveryLocationBhandarewala,
    insertDeliveryLocationBhandarewala,
    updateDeliveryLocationBhandarewala,
  } = useBhandarewala();
  const [locations, setLocations] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [loadingList, setLoadingList] = useState(false);
  const [saving, setSaving] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);

  const sortedLocations = useMemo(
    () =>
      [...locations].sort((a, b) =>
        getArea(a).localeCompare(getArea(b), undefined, {
          sensitivity: "base",
        }),
      ),
    [locations],
  );

  const getDeliveryLocations = useCallback(async () => {
    if (!userID) return;

    setLoadingList(true);

    try {
      const result = await getDeliveryLocationBhandarewala(userID);

      if (!result?.success) {
        setLocations([]);
        return;
      }

      setLocations(Array.isArray(result?.rows) ? result.rows : []);
    } catch (error) {
      console.error("GET BHANDAREWALA DELIVERY LOCATIONS ERROR:", error);
      setLocations([]);
      toast.error("Failed to load delivery locations");
    } finally {
      setLoadingList(false);
    }
  }, [userID]);

  useEffect(() => {
    getDeliveryLocations();
  }, [getDeliveryLocations, refreshKey]);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    let nextValue = value;

    if (name === "delivery_pincode") {
      nextValue = value.replace(/\D/g, "").slice(0, 6);
    }

    if (name === "delivery_price" || name === "delivery_distance") {
      nextValue = value.replace(/[^\d.]/g, "");

      const [whole = "", ...decimalParts] = nextValue.split(".");
      nextValue =
        decimalParts.length > 0
          ? `${whole}.${decimalParts.join("").slice(0, 2)}`
          : whole;
    }

    setForm((prev) => ({
      ...prev,
      [name]: nextValue,
    }));
  };

  const validateForm = () => {
    if (!form.delivery_area.trim()) {
      toast.error("Please enter delivery area");
      return false;
    }

    if (!/^\d{6}$/.test(form.delivery_pincode)) {
      toast.error("Please enter a valid 6-digit pincode");
      return false;
    }

    if (
      form.delivery_price === "" ||
      Number.isNaN(Number(form.delivery_price)) ||
      Number(form.delivery_price) < 0
    ) {
      toast.error("Please enter a valid delivery price");
      return false;
    }

    if (
      form.delivery_distance === "" ||
      Number.isNaN(Number(form.delivery_distance)) ||
      Number(form.delivery_distance) < 0
    ) {
      toast.error("Please enter a valid delivery distance");
      return false;
    }

    return true;
  };

  const insertDeliveryLocation = async () => {
    if (!validateForm() || !userID) return;

    setSaving(true);

    try {
      const result = await insertDeliveryLocationBhandarewala({
        userid: String(userID),
        delivery_area: form.delivery_area.trim(),
        delivery_pincode: String(form.delivery_pincode),
        delivery_price: String(form.delivery_price),
        delivery_distance: String(form.delivery_distance),
      });

      if (!result?.success) return;

      toast.success(
        result?.data?.responseMessage || "Delivery location added successfully",
      );

      resetForm();
      await getDeliveryLocations();
    } catch (error) {
      console.error("INSERT BHANDAREWALA DELIVERY LOCATION ERROR:", error);
      toast.error("Failed to add delivery location");
    } finally {
      setSaving(false);
    }
  };

  const updateDeliveryLocation = async (payload, options = {}) => {
    const { showToast = true, refresh = true } = options;

    const result = await updateDeliveryLocationBhandarewala({
      userid: String(userID),
      id: Number(payload.id),
      delivery_area: String(payload.delivery_area || "").trim(),
      delivery_pincode: String(payload.delivery_pincode || ""),
      delivery_price: String(payload.delivery_price || ""),
      delivery_distance: String(payload.delivery_distance || ""),
      is_active: payload.is_active === "N" ? "N" : "Y",
    });

    if (!result?.success) {
      throw new Error(
        result?.data?.responseMessage || "Failed to update delivery location",
      );
    }

    if (showToast) {
      toast.success(
        result?.data?.responseMessage ||
          "Delivery location updated successfully",
      );
    }

    if (refresh) {
      await getDeliveryLocations();
    }

    return result;
  };

  const handleSave = async () => {
    if (!editingId) {
      await insertDeliveryLocation();
      return;
    }

    if (!validateForm() || !userID) return;

    setSaving(true);

    try {
      await updateDeliveryLocation({
        id: editingId,
        ...form,
      });

      resetForm();
    } catch (error) {
      console.error("UPDATE BHANDAREWALA DELIVERY LOCATION ERROR:", error);
      toast.error(
        error?.response?.data?.responseMessage ||
          error?.response?.data?.message ||
          error?.message ||
          "Failed to update delivery location",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item) => {
    const id = getLocationId(item);

    if (id === undefined || id === null || id === "") {
      toast.error("Location ID missing");
      return;
    }

    setEditingId(id);
    setForm({
      delivery_area: String(getArea(item)),
      delivery_pincode: String(getPincode(item)),
      delivery_price: String(getPrice(item)),
      delivery_distance: String(getDistance(item)),
      is_active: getActive(item),
    });

    window.requestAnimationFrame(() => {
      document
        .getElementById("bhandarewala-location-form")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const handleToggleActive = async (item) => {
    const id = getLocationId(item);

    if (id === undefined || id === null || id === "") {
      toast.error("Location ID missing");
      return;
    }

    setUpdatingId(id);

    try {
      await updateDeliveryLocation(
        {
          id,
          delivery_area: getArea(item),
          delivery_pincode: getPincode(item),
          delivery_price: getPrice(item),
          delivery_distance: getDistance(item),
          is_active: getActive(item) === "Y" ? "N" : "Y",
        },
        { showToast: false },
      );
    } catch (error) {
      console.error("TOGGLE BHANDAREWALA DELIVERY LOCATION ERROR:", error);
      toast.error(
        error?.response?.data?.responseMessage ||
          error?.response?.data?.message ||
          error?.message ||
          "Failed to update location status",
      );
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div
        id="bhandarewala-location-form"
        className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden scroll-mt-6"
      >
        <div className="flex items-center justify-between gap-4 px-5 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-green-50 text-green-700 flex items-center justify-center font-bold text-sm">
              <MapPin className="w-4 h-4" />
            </div>

            <div>
              <h2 className="text-base font-bold text-gray-900">
                {editingId ? "Edit location" : "Add location"}
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Configure delivery area, pincode, price and
                distance.
              </p>
            </div>
          </div>

          {editingId && (
            <button
              type="button"
              onClick={resetForm}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              <X className="w-4 h-4" />
              Cancel
            </button>
          )}
        </div>

        <div className="p-5">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Delivery area
              </label>
              <input
                type="text"
                name="delivery_area"
                value={form.delivery_area}
                onChange={handleChange}
                placeholder="e.g. Noida"
                className="w-full h-11 px-3.5 border border-gray-300 rounded-xl outline-none transition focus:border-green-500 focus:ring-2 focus:ring-green-100"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Pincode
              </label>
              <input
                type="text"
                inputMode="numeric"
                name="delivery_pincode"
                value={form.delivery_pincode}
                onChange={handleChange}
                placeholder="e.g. 201301"
                maxLength={6}
                className="w-full h-11 px-3.5 border border-gray-300 rounded-xl outline-none transition focus:border-green-500 focus:ring-2 focus:ring-green-100"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Delivery price
              </label>
              <div className="relative">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500">
                  ₹
                </span>
                <input
                  type="text"
                  inputMode="decimal"
                  name="delivery_price"
                  value={form.delivery_price}
                  onChange={handleChange}
                  placeholder="0"
                  className="w-full h-11 pl-8 pr-3.5 border border-gray-300 rounded-xl outline-none transition focus:border-green-500 focus:ring-2 focus:ring-green-100"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Distance
              </label>
              <div className="relative">
                <input
                  type="text"
                  inputMode="decimal"
                  name="delivery_distance"
                  value={form.delivery_distance}
                  onChange={handleChange}
                  placeholder="0"
                  className="w-full h-11 px-3.5 pr-12 border border-gray-300 rounded-xl outline-none transition focus:border-green-500 focus:ring-2 focus:ring-green-100"
                />
                <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-xs font-semibold text-gray-400">
                  KM
                </span>
              </div>
            </div>
          </div>

          {editingId && (
            <div className="mt-4 flex items-center gap-3">
              <span className="text-sm font-semibold text-gray-700">
                Status
              </span>
              <button
                type="button"
                onClick={() =>
                  setForm((prev) => ({
                    ...prev,
                    is_active: prev.is_active === "Y" ? "N" : "Y",
                  }))
                }
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
                  form.is_active === "Y" ? "bg-green-600" : "bg-gray-300"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                    form.is_active === "Y" ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
              <span
                className={`text-xs font-bold ${
                  form.is_active === "Y" ? "text-green-700" : "text-gray-500"
                }`}
              >
                {form.is_active === "Y" ? "Active" : "Inactive"}
              </span>
            </div>
          )}

          <div className="mt-5 flex justify-end">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="inline-flex min-w-[150px] items-center justify-center gap-2 px-4 py-2.5 bg-green-600 text-white text-sm font-bold rounded-xl hover:bg-green-700 disabled:bg-green-300 disabled:cursor-not-allowed transition"
            >
              {editingId ? (
                <Save className="w-4 h-4" />
              ) : (
                <Plus className="w-4 h-4" />
              )}
              {saving
                ? editingId
                  ? "Updating..."
                  : "Adding..."
                : editingId
                  ? "Update location"
                  : "Add location"}
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-200">
          <div className="w-7 h-7 rounded-full bg-green-50 text-green-700 flex items-center justify-center text-sm font-bold">
            1
          </div>
          <div>
            <h2 className="text-base font-bold text-gray-900">Delivery area</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {locations.length}{" "}
              {locations.length === 1 ? "location" : "locations"}
            </p>
          </div>
        </div>

        <div className="p-4">
          {loadingList ? (
            <div className="py-12 text-center text-sm text-gray-500">
              Loading locations...
            </div>
          ) : sortedLocations.length === 0 ? (
            <div className="py-12 text-center">
              <MapPin className="w-8 h-8 mx-auto text-gray-300 mb-3" />
              <p className="text-sm font-semibold text-gray-700">
                No delivery locations added yet
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Add the first location using the form above.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-2.5">
              {sortedLocations.map((item, index) => {
                const id = getLocationId(item);
                const isActive = getActive(item) === "Y";
                const isUpdating = String(updatingId) === String(id);

                return (
                  <div
                    key={id ?? `${getArea(item)}-${index}`}
                    className={`flex items-center justify-between gap-3 min-h-[58px] px-3.5 py-2.5 border rounded-xl transition ${
                      isActive
                        ? "border-gray-200 bg-white"
                        : "border-gray-200 bg-gray-50"
                    }`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-8 h-8 flex-shrink-0 rounded-lg border border-gray-200 flex items-center justify-center">
                        <MapPin className="w-4 h-4 text-gray-700" />
                      </div>

                      <div className="min-w-0">
                        <div className="flex items-center gap-2 min-w-0">
                          <p className="font-bold text-sm text-gray-900 truncate">
                            {getArea(item) || "Unnamed area"}
                          </p>

                          <span
                            className={`flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${
                              isActive
                                ? "bg-green-50 text-green-700"
                                : "bg-gray-200 text-gray-600"
                            }`}
                          >
                            {isActive && <Check className="w-3 h-3" />}
                            {isActive ? "Active" : "Inactive"}
                          </span>
                        </div>

                        <p className="text-xs text-gray-500 mt-0.5 truncate">
                          {getPincode(item) || "No pincode"}
                          <span className="mx-1.5">•</span>₹
                          {getPrice(item) || "0"}
                          <span className="mx-1.5">•</span>
                          {getDistance(item) || "0"} km
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => handleToggleActive(item)}
                        disabled={isUpdating}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition disabled:opacity-50 ${
                          isActive ? "bg-green-600" : "bg-gray-300"
                        }`}
                        title={
                          isActive ? "Deactivate location" : "Activate location"
                        }
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                            isActive ? "translate-x-6" : "translate-x-1"
                          }`}
                        />
                      </button>

                      <button
                        type="button"
                        onClick={() => handleEdit(item)}
                        className="w-8 h-8 inline-flex items-center justify-center border border-gray-200 rounded-lg text-gray-600 hover:text-green-700 hover:border-green-200 hover:bg-green-50 transition"
                        title="Edit location"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LocationSettings;
